import type { FieldErrors } from 'react-hook-form'
import { StyleSheet } from 'react-native'
import { Card, HelperText, Text, useTheme } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'

type ErrorEntry = {
    key: string
    message: string
}

const IGNORED_ERROR_KEYS = new Set(['type', 'ref', 'message', 'types'])

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

const flattenErrorValue = (value: unknown, keyPath: string): ErrorEntry[] => {
    if (!value) {
        return []
    }

    if (Array.isArray(value)) {
        return flattenArrayErrors(value, keyPath)
    }

    if (isRecord(value)) {
        return flattenRecordErrors(value, keyPath)
    }

    return typeof value === 'string' ? [{ key: keyPath || 'form', message: value }] : []
}

const flattenArrayErrors = (items: unknown[], keyPath: string): ErrorEntry[] => {
    return items.flatMap((item, index) =>
        flattenErrorValue(item, keyPath ? `${keyPath}[${index}]` : `[${index}]`)
    )
}

const flattenRecordErrors = (record: Record<string, unknown>, keyPath: string): ErrorEntry[] => {
    const currentKey = keyPath || 'form'
    const entries: ErrorEntry[] = []

    entries.push(...extractPrimaryMessage(record, currentKey))
    entries.push(...extractTypeMessages(record, currentKey))
    entries.push(...extractNestedMessages(record, keyPath))

    return entries
}

const extractPrimaryMessage = (record: Record<string, unknown>, key: string): ErrorEntry[] => {
    const message = typeof record.message === 'string' ? record.message : null
    return message ? [{ key, message }] : []
}

const extractTypeMessages = (record: Record<string, unknown>, key: string): ErrorEntry[] => {
    if (!isRecord(record.types)) return []
    return Object.values(record.types)
        .filter((value): value is string => typeof value === 'string')
        .map(message => ({ key, message }))
}

const extractNestedMessages = (record: Record<string, unknown>, keyPath: string): ErrorEntry[] => {
    const entries: ErrorEntry[] = []
    for (const [nestedKey, nestedValue] of Object.entries(record)) {
        if (IGNORED_ERROR_KEYS.has(nestedKey)) continue
        const nextKey = keyPath ? `${keyPath}.${nestedKey}` : nestedKey
        entries.push(...flattenErrorValue(nestedValue, nextKey))
    }
    return entries
}

const extractErrorEntries = (errors: FieldErrors): ErrorEntry[] => {
    return Object.entries(errors).flatMap(([key, value]) => flattenErrorValue(value, key))
}

export interface FormErrorSummaryProps {
    errors: FieldErrors
    title?: string
    testID?: string
    isEnabled?: boolean
}

export function FormErrorSummary({
    errors,
    title = 'Please fix the following errors:',
    testID,
    isEnabled = false,
}: FormErrorSummaryProps) {
    const _theme = useTheme()
    const semanticColors = useSemanticColors()

    if (!isEnabled) {
        return null
    }

    const errorEntries = extractErrorEntries(errors)

    if (errorEntries.length === 0) {
        return null
    }

    return (
        <Card
            mode="outlined"
            style={[
                styles.card,
                {
                    backgroundColor: semanticColors.errorLight,
                    borderColor: semanticColors.errorBorder,
                },
            ]}
            testID={testID}
        >
            <Card.Content>
                <Text variant="titleMedium" style={[styles.title, { color: semanticColors.error }]}>
                    {title}
                </Text>
                {errorEntries.map(entry => (
                    <HelperText
                        key={`${entry.key}-${entry.message}`}
                        type="error"
                        style={styles.error}
                    >
                        {entry.key}: {entry.message || 'Invalid value'}
                    </HelperText>
                ))}
            </Card.Content>
        </Card>
    )
}

const styles = StyleSheet.create({
    card: {
        marginBottom: 8,
    },
    title: {
        marginBottom: 8,
    },
    error: {
        marginBottom: 4,
    },
})
